import React, { Component } from 'react'
import { PropTypes } from 'prop-types'
import { Button, Scroller, Tooltip } from 'dbrain-web-ui-lib'
import Combokeys from 'combokeys'
import uuidv4 from 'uuid/v4'
import { offset, debounce, getX, getY, setNoScroll, path2svg, taskPathToArray, denormalizeArray } from 'Utils/common'

import IconZoomIn from 'Components/Task/types/images/fullscreen-zoomin.inline.svg'
import IconZoomOut from 'Components/Task/types/images/fullscreen-zoomout.inline.svg'
import IconFullscreen from 'Components/Task/types/images/fullscreen.inline.svg'
import IconFullscreenClose from 'Components/Task/types/images/fullscreen-close.inline.svg'

import './style'

const makePoints = (polyLines, width, height) => polyLines.map(point => (
	point && point.length === 2
		? [point[0] * width, point[1] * height]
		: ''
))

const normalizePoints = (pts) => {
	const p = pts.trim()

	return p[p.length - 1] === ','
		? p.slice(0, p.length - 1)
		: p
}

const middlePolygonPoint = (polygon, width, height, stroke) => {
	let minX = polygon[0][0]
	let maxX = polygon[0][0]
	let minY = polygon[0][1]
	let maxY = polygon[0][1]

	polygon.forEach((point) => {
		const x = point[0]
		const y = point[1]

		if (x < minX) minX = x
		if (x > maxX) maxX = x
		if (y < minY) minY = y
		if (y > maxY) maxY = y
	})

	return {
		x: ((minX + maxX) / 2) * width,
		y: (((minY + maxY) / 2) * height) + (stroke * 2),
	}
}

export default class Core extends Component {
	constructor(props) {
		super(props)

		this.state = {
			paths: this.props.paths || {},
			pathId: null,
			isFullscreenTooltipShown: false,
			resultPolygons: [],
			resultPolygonsReady: false,
		}

		this.maxZoom = 2
		this.minZoom = 1
		this.zoomStep = 0.1

		this._onMouseMove = this._onMouseMove.bind(this)
		this._onMouseUp = this._onMouseUp.bind(this)
		this.combokeys = new Combokeys(document.documentElement)
	}

	componentWillMount() {
		this._onResize = debounce(this.setResolution.bind(this), 250)
		window.addEventListener('resize', this._onResize)
		document.addEventListener('mousemove', this._onMouseMove)
		document.addEventListener('mouseup', this._onMouseUp)
		document.addEventListener('touchmove', this._onMouseMove)
		document.addEventListener('touchend', this._onMouseUp)

		this.combokeys.bind('esc', () => { this.handleEscKeyPress() })
	}

	componentDidMount() {
		this.setResolution()
	}

	componentWillReceiveProps(nextProps) {
		if (nextProps.comparedResult !== null && nextProps.isNeedToShowTaskResults) {
			this.setState({ resultPolygons: [], resultPolygonsReady: false }, () => {
				this.renderResultRects(nextProps.comparedResult.result)
			})
		} else {
			this.setState({ resultPolygons: [], resultPolygonsReady: false })
		}

		const equal = JSON.stringify(Object.keys(nextProps.paths)) === JSON.stringify(Object.keys(this.props.paths))
		if (!equal) {
			this._mouseup = null
			this.setState({ paths: nextProps.paths }, () => {
				this.setResolution()
			})
		} else {
			this.setResolution()
		}

		const nextNP = nextProps.normalizedPoints
		const lastNP = this.props.normalizedPoints

		if (
			nextNP &&
			Object.keys(nextNP).toString() !== Object.keys(lastNP).toString()
		) {
			this.rerenderPath(nextNP)
		}
	}

	componentWillUnmount() {
		window.removeEventListener('resize', this._onResize)
		document.removeEventListener('mousemove', this._onMouseMove)
		document.removeEventListener('mouseup', this._onMouseUp)
		document.removeEventListener('touchmove', this._onMouseMove)
		document.removeEventListener('touchend', this._onMouseUp)
	}

	setResolution() {
		const { paths } = this.state
		Object.keys(paths).forEach((pathId) => {
			if (Object.prototype.hasOwnProperty.call(paths, pathId)) {
				this._fixPath(paths[pathId], true)
			}
		})
		this.setState({ paths })
	}

	rerenderPath(object) {
		const size = [this.svgarea.clientWidth, this.svgarea.clientHeight]
		const { paths } = this.state

		Object.keys(object).forEach((id) => {
			const current = object[id]
			const localPath = denormalizeArray(current, size)

			paths[id].path = localPath
			this.rebuildRects(localPath, id, size)
		})

		this.setState({ paths })
		this.props.onPathsUpdate(paths, null)
	}

	rebuildRects(path, id) {
		const d = path2svg(0, 0, path, false)
		const { paths } = this.state

		this[id].querySelector('path').setAttribute('d', d.path)
		paths[id].path = path
		this.setState({ paths })
		this.props.onPathsUpdate(paths, null)
	}

	handleEscKeyPress() {
		const { pathId, paths } = this.state

		delete paths[pathId]
		this.setState({ paths, pathId: null })
	}

	_onMouseMove(e) {
		if (this._mousemove) {
			this._mousemove(e)
		}
	}

	_onMouseUp(e) {
		if (this._mouseup) {
			this._mouseup(e)
		}
	}

	closePath(pathId, event) {
		event.stopPropagation()
		event.preventDefault()

		const { paths } = this.state

		if (pathId) {
			if (paths[pathId].path.length < 3) {
				this.deletePath(pathId, event)
			} else {
				paths[pathId].closed = true
				paths[pathId].createdAt = +new Date()
				paths[pathId].showDelete = true
				this._mousemove = null
				this._fixPath(paths[pathId])
				this.setState({ paths, pathId: null })

				const pathArray = taskPathToArray(paths[pathId].path)
				this.props.normalizePoints(pathArray, pathId)
			}
		}
	}

	_fixPath(path, reverse) {
		const w = this.props.imageRef.current.offsetWidth
		const h = this.props.imageRef.current.offsetHeight
		let x1 = w + 1
		let y1 = h + 1
		let x2 = -1
		let y2 = -1
		for (let i = 0; i < path.path.length; i += 1) {
			if (reverse) {
				path.path[i].rx = Math.min(Math.max(path.path[i].rx, 0), 1)
				path.path[i].ry = Math.min(Math.max(path.path[i].ry, 0), 1)
				path.path[i].x = path.path[i].rx * w
				path.path[i].y = path.path[i].ry * h
			} else {
				path.path[i].x = Math.min(Math.max(path.path[i].x, 0), w)
				path.path[i].y = Math.min(Math.max(path.path[i].y, 0), h)
				path.path[i].rx = path.path[i].x / w
				path.path[i].ry = path.path[i].y / h
			}
			x1 = Math.min(x1, path.path[i].x)
			y1 = Math.min(y1, path.path[i].y)
			x2 = Math.max(x2, path.path[i].x)
			y2 = Math.max(y2, path.path[i].y)
		}
		if (reverse) {
			path.rx = Math.min(Math.max(path.rx, 0), 1)
			path.ry = Math.min(Math.max(path.ry, 0), 1)
			path.x = path.rx * w
			path.y = path.ry * h
		} else {
			path.x = Math.min(Math.max(path.x, 0), w)
			path.y = Math.min(Math.max(path.y, 0), h)
			path.rx = path.x / w
			path.ry = path.y / h
		}
		if (!path.closed) {
			x1 = Math.min(x1, path.x)
			y1 = Math.min(y1, path.y)
			x2 = Math.max(x2, path.x)
			y2 = Math.max(y2, path.y)
		}
		path.bboxX = x1
		path.bboxY = y1
		path.bboxWidth = x2 - x1
		path.bboxHeight = y2 - y1
		if (reverse) {
			path.bboxX = path.rBboxX * w
			path.bboxY = path.rBboxY * h
			path.bboxWidth = path.rBboxWidth * w
			path.bboxHeight = path.rBboxHeight * h
		} else {
			path.rBboxX = path.bboxX / w
			path.rBboxY = path.bboxY / h
			path.rBboxWidth = path.bboxWidth / w
			path.rBboxHeight = path.bboxHeight / h
		}
		if (path.closed) {
			path.svg = path2svg(0, 0, path.path, false)
		} else {
			const p = [].concat(path.path, [path])
			path.svg = path2svg(0, 0, p, true)
		}
	}

	createPath(downEvent) {
		const {
			onPathsUpdate,
			label,
			idx,
			pathStyle,
		} = this.props
		const { paths } = this.state
		let { pathId } = this.state

		if (!label) {
			return
		}

		downEvent.stopPropagation()
		downEvent.preventDefault()

		setNoScroll(true)

		const startX = getX(downEvent)
		const startY = getY(downEvent)

		const { top, left } = offset(this.props.imageRef.current)
		const x = startX - left
		const y = startY - top

		if (pathId) {
			paths[pathId].path.push({ x, y })
		} else {
			pathId = uuidv4()
			paths[pathId] = {
				label,
				style: pathStyle,
				idx,
				showDelete: false,
				path: [{ x, y }],
				x,
				y,
				bboxX: x,
				bboxY: y,
				bboxWidth: 0,
				bboxHeight: 0,
			}
		}

		this._fixPath(paths[pathId])
		this.setState({ pathId, paths })

		this._mousemove = (moveEvent) => {
			moveEvent.preventDefault()
			moveEvent.stopPropagation()

			if (!this.state.pathId) return

			const o = offset(this.props.imageRef.current)
			const _x = getX(moveEvent) - o.left
			const _y = getY(moveEvent) - o.top
			const _paths = this.state.paths
			const path = _paths[pathId]

			path.x = _x
			path.y = _y
			this._fixPath(path)

			if (path && !path.showControls) {
				this.switchPathControls(pathId, true)
			}

			this.setState({ paths: _paths })
		}

		this._mouseup = () => {
			setNoScroll(false)
			this._mouseup = null
			onPathsUpdate(paths)
			this.props.mouseDownHandler(false)
		}
	}

	deletePath(_pathId, e) {
		if (e) {
			e.stopPropagation()
			e.preventDefault()
		}
		const { paths, pathId } = this.state
		const { onPathsUpdate } = this.props
		delete paths[_pathId]
		if (_pathId === pathId) {
			this.setState({ paths, pathId: null })
		} else {
			this.setState({ paths })
		}
		onPathsUpdate(paths)
	}

	movePoint(pathId, idx, e) {
		const { paths } = this.state
		const { onPathsUpdate, label } = this.props
		const movingPath = paths[pathId]

		if (!label || movingPath.label !== label) {
			this.props.mouseDownHandler(false)
			return
		}

		e.stopPropagation()
		e.preventDefault()

		this.props.mouseDownHandler(true)

		if (!movingPath.showControls) {
			this.switchPathControls(pathId, true)
		}
		setNoScroll(true)
		// movingPath.showDelete = false
		const startX = getX(e)
		const startY = getY(e)
		const x0 = movingPath.path[idx].x
		const y0 = movingPath.path[idx].y

		this.__mousemove = this._mousemove
		this.__mouseup = this._mouseup

		this._mousemove = (moveEvent) => {
			moveEvent.preventDefault()
			moveEvent.stopPropagation()
			const x = getX(moveEvent)
			const y = getY(moveEvent)
			movingPath.path[idx].x = (x0 + x) - startX
			movingPath.path[idx].y = (y0 + y) - startY
			movingPath.x = (x0 + x) - startX
			movingPath.y = (y0 + y) - startY
			this._fixPath(movingPath)
			if (movingPath && !movingPath.showControls) {
				this.switchPathControls(pathId, true)
			}
			paths[pathId] = movingPath
			this.setState({ paths })
		}
		this._mouseup = () => {
			// movingPath.showDelete = true
			setNoScroll(false)
			this._mousemove = null
			onPathsUpdate(paths)
			this._mousemove = this.__mousemove
			this._mouseup = this.__mouseup
		}
	}

	handleSvgAreaMouseup() {
		this.props.mouseDownHandler(false)
	}

	buttonToggleFullscreenHover(isFullscreenTooltipShown) {
		this.setState({ isFullscreenTooltipShown })
	}

	switchPathControls(pathId, isVisible) {
		const { isNeedToShowTaskResults } = this.props
		const { paths } = this.state

		if (isNeedToShowTaskResults) return

		Object.keys(paths).forEach((rId) => {
			if (Object.prototype.hasOwnProperty.call(paths, rId)) {
				paths[rId].showControls = false
			}
		})
		paths[pathId].showControls = isVisible
		this.setState({ paths })
	}

	undo(e) {
		const { paths, pathId } = this.state

		e.preventDefault()

		if (pathId === null) {
			let newest = 0
			let newestUuid

			Object.keys(paths).forEach((i) => {
				const current = paths[i].createdAt

				if (current > newest) {
					newest = current
					newestUuid = i
				}
			})

			delete paths[newestUuid]
		} else {
			delete paths[pathId]
		}


		this.props.onPathsUpdate(paths)
		this.setState({ paths, pathId: null })
	}

	clear(e) {
		const paths = {}
		const pathId = null

		e.preventDefault()

		this.setState({ paths, pathId })
		this.props.clearPaths()
	}

	handleZoomButtonClick(type, callback) {
		const { zoomRatioPercentage, changeZoomRatio } = this.props

		const zoomRatio = type === 'up'
			? zoomRatioPercentage + this.zoomStep
			: zoomRatioPercentage - this.zoomStep

		changeZoomRatio(parseFloat(zoomRatio.toFixed(1)), callback)
	}

	renderPathControls(pathId) {
		const { paths } = this.state
		const path = paths[pathId]
		const delSize = 9
		const crossRadius = delSize * (2 / 3)
		const crossLineSize = Math.sqrt((crossRadius * crossRadius) / 2)
		let del = ''
		if (path.showDelete) {
			const bbX = path.bboxX + (path.bboxWidth / 2)
			const bbY = path.bboxY + (path.bboxHeight / 2)
			del = (
				<g>
					<circle
						cx={bbX}
						cy={bbY}
						r={delSize}
						style={{ fill: path.style.stroke }}
					/>
					<line
						x1={bbX - crossLineSize}
						y1={bbY - crossLineSize}
						x2={bbX + crossLineSize}
						y2={bbY + crossLineSize}
						style={{ stroke: 'white', strokeWidth: 2 }}
					/>
					<line
						x1={bbX + crossLineSize}
						y1={bbY - crossLineSize}
						x2={bbX - crossLineSize}
						y2={bbY + crossLineSize}
						style={{ stroke: 'white', strokeWidth: 2 }}
					/>
					<circle
						className="path_delete transparent"
						onMouseDown={this.deletePath.bind(this, pathId)}
						onTouchStart={this.deletePath.bind(this, pathId)}
						cx={bbX}
						cy={bbY}
						r={delSize}
					/>
				</g>
			)
		}
		if (path.label !== this.props.label) {
			return (
				<g>
					{del}
				</g>
			)
		}
		const points = path.path.map((point, idx) => {
			const close = idx === 0 && !path.closed
			const closeRadius = 6
			const defaultRadius = 3

			return (
				<circle
					className={`point${close ? ' close' : ''}`}
					onTouchStart={close ? this.closePath.bind(this, pathId) : this.movePoint.bind(this, pathId, idx)}
					onMouseDown={close ? this.closePath.bind(this, pathId) : this.movePoint.bind(this, pathId, idx)}
					cx={point.x}
					cy={point.y}
					r={close ? closeRadius : defaultRadius}
					style={{ fill: path.style.stroke }}
					key={idx}
				/>
			)
		})
		return (
			<g>
				{points}
				{del}
			</g>
		)
	}

	renderPath(pathId) {
		const path = this.state.paths[pathId]

		if (!path) return ''

		const { isMarkButtonInCaption } = this.props
		const {
			bboxWidth,
			bboxHeight,
			bboxX,
			bboxY,
			idx,
			closed,
		} = path
		const { stroke, strokeWidth, fontSize } = path.style

		return (
			<g
				onMouseEnter={this.switchPathControls.bind(this, pathId, true)}
				onMouseLeave={this.switchPathControls.bind(this, pathId, false)}
				key={pathId}
				ref={(c) => { this[pathId] = c }}
			>
				<path
					d={path.svg.path}
					className="path"
					style={path.style}
				/>

				{
					isMarkButtonInCaption
						? ''
						: (
							<text
								x={bboxX + (bboxWidth / 2)}
								y={(bboxY - strokeWidth) + (bboxHeight / 2) + (fontSize / 2)}
								fill={stroke}
								fontSize={fontSize}
								style={{ display: closed ? 'block' : 'none' }}
							>
								<tspan textAnchor="middle">{idx}</tspan>
							</text>
						)
				}

				{path.showControls ? this.renderPathControls(pathId) : ''}
			</g>
		)
	}

	renderResultRects(result) {
		const { isNeedToShowTaskResults } = this.props

		if (!isNeedToShowTaskResults) {
			this.setState({ resultPolygons: [] })
		}

		const resultPolygons = []
		const clientWidth = this.svgarea.clientWidth || this.svgarea.parentNode.clientWidth
		const clientHeight = this.svgarea.clientHeight || this.svgarea.parentNode.clientHeight
		const resultTypes = Object.keys(result)
		const style = {
			fill: 'rgba(42, 193, 120, 0.5)',
			fontSize: '16',
			stroke: '#2AC178',
			strokeWidth: 2,
		}

		resultTypes.forEach((resType, num) => {
			const polygonsTypes = result[resType]

			if (polygonsTypes.length) {
				polygonsTypes.forEach((polyarr) => {
					const polygon = polyarr[0]
					const p = makePoints(polygon, clientWidth, clientHeight).join(', ')
					const middle = middlePolygonPoint(polygon, clientWidth, clientHeight, style.strokeWidth)

					const group = (
						<g className="lower" key={uuidv4()}>
							<polygon
								points={normalizePoints(p)}
								style={style}
							/>
							<text
								x={middle.x}
								y={middle.y}
								fill={style.stroke}
								fontSize={style.fontSize}
							>
								<tspan textAnchor="middle">{num + 1}</tspan>
							</text>
						</g>
					)

					resultPolygons.push(group)
				})
			}
		})

		this.setState({ resultPolygons, resultPolygonsReady: true })
	}

	renderResultRectsElements() {
		const { resultPolygons, resultPolygonsReady } = this.state

		if (resultPolygons.length && resultPolygonsReady) {
			return resultPolygons.map(poly => poly)
		}

		return ''
	}

	render() {
		const {
			imageUrl,
			zoomRatioPercentage,
		} = this.props
		const { paths, pathId } = this.state

		const pathsElements1 = []
		const pathsElements2 = []
		const pathsElements3 = []
		Object.keys(paths).forEach((id) => {
			if (id === pathId) {
				return
			}
			if (paths[id].label !== this.props.label) {
				pathsElements1.push(this.renderPath(id))
			} else if (!paths[id].showControls) {
				pathsElements2.push(this.renderPath(id))
			} else {
				pathsElements3.push(this.renderPath(id))
			}
		})
		const pathsElements = pathsElements1.concat(pathsElements2, pathsElements3)

		if (pathId) {
			pathsElements.push(this.renderPath(pathId))
		}

		const outerSize = {
			width: document.body.offsetWidth,
			height: document.body.offsetHeight,
		}

		return (
			<React.Fragment>
				<div
					className="core-middle"
					ref={this.props.coreMiddleRef}
				>
					<Button
						className="btn btn-dark-gray core-button-fullscreenClose"
						onClick={() => {
							this.props.handleButtonToggleFullscreen(1, this.setResolution.bind(this))
						}}
					>
						<IconFullscreenClose />
					</Button>

					<div className="core-zoom">
						<Button
							className="btn btn-dark-gray core-button-zoom-in"
							onClick={() => { this.handleZoomButtonClick('up', this.setResolution.bind(this)) }}
							disabled={zoomRatioPercentage >= this.maxZoom}
						>
							<IconZoomIn />
						</Button>

						<Button
							className="btn btn-dark-gray core-button-zoom-out"
							onClick={() => { this.handleZoomButtonClick('down', this.setResolution.bind(this)) }}
							disabled={zoomRatioPercentage <= this.minZoom}
						>
							<IconZoomOut />
						</Button>

						<span className="core-zoom-percentage">
							{`${parseInt(zoomRatioPercentage * 100, 10)}%`}
						</span>
					</div>

					<div className="core-inner">
						<Scroller
							disabled={!this.props.isFullscreen}
							outerSize={outerSize}
							innerSize={this.props.innerFullscreenSize}
							className="core-scroller"
							fullscreenOffsetLeft={this.props.fullscreenOffsetLeft}
							fullscreenOffsetTop={this.props.fullscreenOffsetTop}
							scrollHeight={8}
						>
							<div
								className={this.props.container}
								ref={this.props.coreref}
								style={this.props.imageFullscreenSize}
							>
								<img
									src={imageUrl}
									alt=""
									ref={this.props.imageRef}
									className="image-value"
								/>
								<svg
									className="svg-area"
									ref={(c) => { this.svgarea = c }}
									onMouseDown={this.createPath.bind(this)}
									onMouseUp={this.handleSvgAreaMouseup.bind(this)}
									onTouchStart={this.createPath.bind(this)}
								>
									{this.renderResultRectsElements()}
									{pathsElements}
								</svg>

								<div className="svg-button-fullscreen">
									<Tooltip
										text="Fullscreen"
										position="bottom-right"
										show={this.state.isFullscreenTooltipShown}
									>
										<span
											tabIndex="0"
											role="button"
											onClick={() => {
												this.props.handleButtonToggleFullscreen(
													1,
													this.setResolution.bind(this),
												)
											}}
											onMouseEnter={() => { this.buttonToggleFullscreenHover(true) }}
											onMouseLeave={() => { this.buttonToggleFullscreenHover(false) }}
										>
											<IconFullscreen />
										</span>
									</Tooltip>
								</div>
							</div>
						</Scroller>

						<div className="svg-bottom">
							<div className="links">
								<Button
									className="link"
									onClick={e => this.undo(e)}
								>
									Undo
								</Button>
								<Button
									className="link"
									onClick={e => this.clear(e)}
								>
									Clear all
								</Button>
							</div>
						</div>
					</div>
				</div>
			</React.Fragment>
		)
	}
}

Core.defaultProps = {
	imageUrl: null,
	paths: {},
	label: null,
	idx: null,
	coreref: null,
	clearPaths: null,
	normalizedPoints: {},
	isFullscreen: false,
	comparedResult: {},
}

Core.propTypes = {
	container: PropTypes.string.isRequired,
	coreref: PropTypes.func,
	imageUrl: PropTypes.string,
	paths: PropTypes.object,
	label: PropTypes.string,
	idx: PropTypes.number,
	clearPaths: PropTypes.func,
	pathStyle: PropTypes.object.isRequired,
	onPathsUpdate: PropTypes.func.isRequired,
	normalizePoints: PropTypes.func.isRequired,
	normalizedPoints: PropTypes.object,
	imageRef: PropTypes.object.isRequired,
	coreMiddleRef: PropTypes.object.isRequired,
	mouseDownHandler: PropTypes.func.isRequired,
	innerFullscreenSize: PropTypes.object.isRequired,
	handleButtonToggleFullscreen: PropTypes.func.isRequired,
	isFullscreen: PropTypes.bool,
	imageFullscreenSize: PropTypes.object.isRequired,
	fullscreenOffsetLeft: PropTypes.number.isRequired,
	fullscreenOffsetTop: PropTypes.number.isRequired,
	zoomRatioPercentage: PropTypes.number.isRequired,
	changeZoomRatio: PropTypes.func.isRequired,
	isMarkButtonInCaption: PropTypes.bool.isRequired,
	comparedResult: PropTypes.object,
	isNeedToShowTaskResults: PropTypes.bool.isRequired,
}
